import React, { useEffect, useMemo, useState } from 'react';
import { Card, Space, Input, Select, Tooltip, Typography } from 'antd';
import { ReloadOutlined, LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatClntMaster } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const MatClntMasterList: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetMatClntMaster() as any;
  const rows: Array<{ itemcode: string; description: string; size_text?: string | null; unit?: string | null; material_group?: string | null }> = useMemo(() => {
    const raw = (Array.isArray(data) ? data : (data?.data ?? [])) as any[];
    return (raw || []).map(r => ({
      itemcode: r.itemcode,
      description: (r.long_desc || r.short_desc || '') as string,
      size_text: (r.size_text ?? null) as string | null,
      unit: (r.unit ?? null) as string | null,
      material_group: (r.material_group ?? null) as string | null,
    }));
  }, [data]);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [unit, setUnit] = useState<string | undefined>(undefined);
  const [group, setGroup] = useState<string | undefined>(undefined);
  const [size, setSize] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Base search filter function (AND across tokens, supports quoted phrases)
  const matchesSearch = useMemo(() => {
    const searchVal = (debouncedSearch || '').toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^\"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    return (row: any) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      try {
        const hay = [row.itemcode, row.description, row.size_text, row.unit, row.material_group]
          .map(v => (v == null ? '' : String(v)))
          .join(' ')
          .toLowerCase();
        if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
        return tokens.every(t => hay.includes(t));
      } catch {
        return false;
      }
    };
  }, [debouncedSearch]);

  // Cascading option sets (respect current other filters + search)
  const unitOptions = useMemo(() => {
    const set = new Map<string, string>();
    rows
      .filter(r => !size || (r.size_text || '') === size)
      .filter(r => !group || (r.material_group || '') === group)
      .filter(r => matchesSearch(r))
      .forEach(r => {
        const raw = (r.unit || '').trim();
        if (!raw) return;
        const key = raw.toLowerCase();
        if (!set.has(key)) set.set(key, raw);
      });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [rows, size, group, matchesSearch]);

  const sizeOptions = useMemo(() => {
    const set = new Map<string, string>();
    rows
      .filter(r => !unit || (r.unit || '') === unit)
      .filter(r => !group || (r.material_group || '') === group)
      .filter(r => matchesSearch(r))
      .forEach(r => {
        const raw = (r.size_text || '').trim();
        if (!raw) return;
        const key = raw.toLowerCase();
        if (!set.has(key)) set.set(key, raw);
      });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [rows, unit, group, matchesSearch]);

  const groupOptions = useMemo(() => {
    const set = new Map<string, string>();
    rows
      .filter(r => !unit || (r.unit || '') === unit)
      .filter(r => !size || (r.size_text || '') === size)
      .filter(r => matchesSearch(r))
      .forEach(r => {
        const raw = (r.material_group || '').trim();
        if (!raw) return;
        const key = raw.toLowerCase();
        if (!set.has(key)) set.set(key, raw);
      });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [rows, unit, size, matchesSearch]);

  const filtered = useMemo(() => {
    return rows.filter(r => (
      (!unit || (r.unit || '') === unit) &&
      (!size || (r.size_text || '') === size) &&
      (!group || (r.material_group || '') === group) &&
      matchesSearch(r)
    ));
  }, [rows, unit, size, group, matchesSearch]);

  // Reset page when filters/search change
  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, unit, size, group]);

  const tooltipProps = useMemo(() => ({
    placement: 'topLeft' as const,
    overlayStyle: { zIndex: 2000, maxWidth: 600, wordBreak: 'break-word' as const },
    autoAdjustOverflow: true,
    mouseEnterDelay: 0.1,
  }), []);

  const columns = useMemo(() => [
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 100, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Description', dataIndex: 'description', key: 'description', width: 500,
      render: (text: string) => (
        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</span>
      )
    },
    { title: 'Group', dataIndex: 'material_group', key: 'material_group', width: 100, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 80, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 40, ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip {...tooltipProps} title={text}>
          <span style={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
        </Tooltip>
      )
    },
  ], [tooltipProps]);

  const handleReset = () => {
    setSearch('');
    setUnit(undefined);
    setGroup(undefined);
    setSize(undefined);
    setCurrentPage(1);
  };

  return (
    <Card title={(
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <Button icon={<LeftOutlined />} type="text" aria-label="Back" onClick={() => navigate(-1)} />
        <span style={{ fontSize: 24, fontWeight: 600 }}>Client Master Materials</span>
      </span>
    )} style={{ margin: 24 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset Filters</Button>
          <Select
            allowClear
            showSearch
            placeholder="Unit"
            style={{ width: 140 }}
            value={unit}
            onChange={(v) => setUnit(v)}
            options={unitOptions}
            filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
          />
          <Select
            allowClear
            showSearch
            placeholder="Group"
            style={{ width: 180 }}
            value={group}
            onChange={(v) => setGroup(v)}
            options={groupOptions}
            filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
          />
          <Select
            allowClear
            showSearch
            placeholder="Size"
            style={{ width: 140 }}
            value={size}
            onChange={(v) => setSize(v)}
            options={sizeOptions}
            filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
          />
          <Input.Search
            placeholder='Search description, itemcode, size, unit (use "quoted phrases")'
            allowClear
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 340 }}
          />
          <Typography.Text type="secondary">{filtered.length.toLocaleString()} items</Typography.Text>
        </div>
        <StandardTable
          columns={columns as any}
          dataSource={filtered}
          rowKey="itemcode"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filtered.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} / ${total} kayıt`,
          }}
          onChange={(p: any) => { setCurrentPage(p.current); setPageSize(p.pageSize); }}
          size="small"
          className="compact-table zebra-table left-align-table"
          rowClassName={(_, index) => (index % 2 === 0 ? 'zebra-row' : '')}
          style={{ padding: '2px' }}
          scroll={{ x: 'max-content' }}
        />
      </Space>
    </Card>
  );
};

export default MatClntMasterList;
